/**
 * CookieBanner — RGPD Cookie Consent
 * Design: Bold Craft — fundo escuro, acentos teal
 * Sem ícones. Sem travessões.
 */

import { useState, useEffect } from "react";
import { Link } from "wouter";
import {
  ANALYTICS_ENABLED_EVENT,
  GA4_MEASUREMENT_ID,
  GOOGLE_ADS_ID,
} from "@/lib/analytics";

const COOKIE_KEY = "sw_cookie_consent";

type ConsentState = "accepted" | "rejected" | null;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[][];
  gtag?: (...args: unknown[]) => void;
};

function appendScript(id: string, src: string, attributes: Record<string, string> = {}) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  script.async = true;
  Object.entries(attributes).forEach(([key, value]) => script.setAttribute(key, value));
  document.head.appendChild(script);
}

function enableAnalytics() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const analyticsWindow = window as AnalyticsWindow & { gtag: (...args: unknown[]) => void };
  window.dataLayer = window.dataLayer || [];
  const dataLayer = window.dataLayer as unknown as IArguments[];
  function gtag() {
    dataLayer.push(arguments);
  }
  window.gtag = gtag;

  appendScript(
    "serviway-google-tag",
    `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`,
  );
  analyticsWindow.gtag("js", new Date());
  analyticsWindow.gtag("config", GA4_MEASUREMENT_ID);
  analyticsWindow.gtag("config", GOOGLE_ADS_ID);

  const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;
  if (endpoint && websiteId) {
    appendScript(
      "serviway-umami",
      `${endpoint.replace(/\/$/, "")}/umami`,
      { defer: "", "data-website-id": websiteId },
    );
  }

  window.dispatchEvent(new Event(ANALYTICS_ENABLED_EVENT));
}

export default function CookieBanner() {
  const [consent, setConsent] = useState<ConsentState>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY) as ConsentState | null;
    if (!stored) {
      // Small delay so banner doesn't flash on first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    setConsent(stored);
    // If already accepted, load analytics
    if (stored === "accepted") {
      enableAnalytics();
    }
  }, []);

  function handleAccept() {
    localStorage.setItem(COOKIE_KEY, "accepted");
    setConsent("accepted");
    setVisible(false);
    enableAnalytics();
  }

  function handleReject() {
    localStorage.setItem(COOKIE_KEY, "rejected");
    setConsent("rejected");
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{
        background: "rgba(10, 15, 26, 0.97)",
        borderTop: "1px solid rgba(0, 180, 200, 0.2)",
        backdropFilter: "blur(8px)",
      }}
      role="dialog"
      aria-label="Consentimento de cookies"
    >
      <div className="max-w-6xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p
            className="text-white text-sm leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Utilizamos cookies para garantir o funcionamento do website e melhorar a sua
            experiência. Os cookies essenciais são sempre ativos. Os cookies analíticos
            requerem o seu consentimento, em conformidade com o RGPD.{" "}
            <Link
              href="/politica-de-privacidade"
              className="inline-flex min-h-[44px] min-w-[44px] items-center underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              style={{ color: "var(--sw-teal-bright)" }}
            >
              Política de Privacidade
            </Link>
            {" "}e{" "}
            <Link
              href="/termos-e-condicoes"
              className="inline-flex min-h-[44px] min-w-[44px] items-center underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              style={{ color: "var(--sw-teal-bright)" }}
            >
              Termos e Condições
            </Link>
            .
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleReject}
            className="min-h-[44px] min-w-[44px] px-5 py-2 text-sm font-semibold border rounded transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            style={{
              borderColor: "rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.7)",
              background: "transparent",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.5)";
              (e.currentTarget as HTMLButtonElement).style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.2)";
              (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.7)";
            }}
          >
            Rejeitar opcionais
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="min-h-[44px] min-w-[44px] px-5 py-2 text-sm font-semibold rounded transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            style={{
              background: "var(--sw-teal)",
              color: "#fff",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "var(--sw-teal)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "var(--sw-teal)";
            }}
          >
            Aceitar todos
          </button>
        </div>
      </div>
    </div>
  );
}
